import { useEffect, useMemo } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { useRedirects, findRedirect } from "@/lib/redirects";
import { GUIDES } from "@/lib/guides";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static client-side redirect map — built once from the GUIDES registry.
// For every published guide whose slug ends with -2026 we register two
// keys that both target the canonical `/guides/{slug}` URL:
//
//   1. Short-slug form (no "-2026" suffix): "/bathroom-remodel-cost-california"
//      → 850+ inbound links from the pre-2026-06 sitemap era.
//   2. Full-slug form (with "-2026" suffix at root):
//      "/bathroom-remodel-cost-california-2026" → hallucinated by GSC /
//      ChatGPT / Perplexity that drop the "/guides/" prefix (surfaced
//      2026-07 via /admin 404s tab; solar-panel-cost-*-2026 wave: 9+
//      hits per state, 5-6 days).
//
// Both forms redirect to the canonical /guides/{slug} URL so any
// external/inbound link resolves cleanly with zero backend calls.
const STATIC_SHORT_SLUG_REDIRECTS = (() => {
  const map = new Map();
  for (const g of GUIDES) {
    if (!g?.slug || !g.slug.endsWith("-2026")) continue;
    const target = "/guides/" + g.slug;
    // Short-slug form: strip "-2026" suffix
    map.set("/" + g.slug.slice(0, -5), target);
    // Full-slug form: guide slug appears at the root (missing /guides/ prefix)
    map.set("/" + g.slug, target);
  }
  return map;
})();

export default function NotFoundPage() {
  const location = useLocation();

  // FIRST priority redirect check: static GUIDES-derived map (instant,
  // covers the 850-short-slug pattern from the 2026-06 P0 sitemap fix).
  // useMemo so the lookup runs once per pathname change instead of every
  // render.
  const staticTarget = useMemo(() => {
    const k = location.pathname.toLowerCase().replace(/\/$/, "");
    return STATIC_SHORT_SLUG_REDIRECTS.get(k) || null;
  }, [location.pathname]);

  // Check the admin-managed 301-redirect table BEFORE rendering the 404.
  // Lets us recover stale URLs (old slugs, mis-typed inbound links, etc.)
  // without redeploying — admins add mappings via /admin's "404s" tab.
  const { redirects, loading } = useRedirects();
  const redirectTarget = staticTarget || findRedirect(location.pathname, redirects);

  // Fire-and-forget 404 telemetry — backend dedupes per (path, day) so a bot
  // probing 100 dead URLs gives us 100 distinct rows (each useful) but a
  // single user refreshing the same dead page 50 times only counts as 50
  // hits on one row. No PII, no blocking, no error handling — if it fails
  // the visitor sees the page anyway. SKIPPED when we have a known
  // redirect target (no point recording a 404 that we already auto-fix).
  useEffect(() => {
    if (loading || redirectTarget) return;
    const path = location.pathname + (location.search || "");
    axios
      .post(`${API}/analytics/404`, {
        path,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
      })
      .catch(() => {});
  }, [location.pathname, location.search, loading, redirectTarget]);

  // Fast-path: if the client-side static map already knows the answer, fire
  // the redirect immediately — no need to wait for the /api/redirects fetch
  // (which can hang up to 30s if Mongo is slow/down, per iteration_64 RCA).
  // All hooks (useMemo, useRedirects, useEffect) run above so this early
  // return doesn't break the Rules of Hooks.
  if (staticTarget) {
    return <Navigate to={staticTarget} replace />;
  }

  // Brief loading state while the redirect table fetches — usually <100ms.
  // Avoids a flash of "404" before redirecting visitors with a known
  // mapping. The fetch is cached module-wide so subsequent 404 hits
  // resolve instantly.
  if (loading) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6 lg:px-8" aria-busy="true">
        <p className="text-sm text-slate-400">Loading…</p>
      </section>
    );
  }

  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  return (
    <>
      {/*
        Self-canonical + noindex,follow is the correct signal for SPA "not found" responses
        (the static server returns 200 for any client-side route). This avoids the Google
        Search Console "Soft 404" error caused by a canonical pointing to /404.
      */}
      <SEO
        title="Page not found | HavenCostGuide"
        description="The page you're looking for doesn't exist."
        noindex
      />
      <section className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <p className="text-xs font-medium uppercase tracking-wider text-green-700">404</p>
        <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
          We couldn't find that page.
        </h1>
        <p className="mt-4 text-base text-slate-600">
          Let's get you back to a calculator or guide.
        </p>
        <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/"
            className="inline-flex h-12 items-center rounded-full bg-slate-900 px-7 text-sm font-semibold text-white hover:bg-slate-800"
            data-testid="not-found-home-link"
          >
            Go home
          </Link>
          <Link
            to="/guides"
            className="inline-flex h-12 items-center rounded-full border border-slate-300 px-7 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            data-testid="not-found-guides-link"
          >
            Browse guides
          </Link>
        </div>
      </section>
    </>
  );
}
